import React, { useContext, useState, useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { Container } from '@sm/wds-react';
import { BasePage, Helmet, StaticContext, SkeletonLoader } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';
import { ThemeProvider } from 'react-jss';

import theme from '../../styles/SurveyMonkey.theme';
import GetCollectorClosedByIdQuery from '~app/pages/SurveyClosed/GetCollectorClosedById.graphql';

import BrandedSurveyClosed from '~app/pages/SurveyClosed/BrandedSurveyClosed';
import EUBrandedSurveyClosed from '~app/pages/SurveyClosed/EUBrandedSurveyClosed';
import NonBrandedSurveyClosed from '~app/pages/SurveyClosed/NonBrandedSurveyClosed';

const COPY = defineMessages({
  DEFAULT_CLOSED_MESSAGE: {
    id: 'SurveyClosed.closedMessage',
    defaultMessage:
      'This survey is currently closed.  Please contact the author of this survey for further assistance.',
  },
  META_KEYWORDS: {
    id: 'SurveyClosed.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  CLOSED_PAGE_TITLE: {
    id: 'SurveyClosed.title',
    defaultMessage: 'Free Online Survey Software by SurveyMonkey: Closed Survey',
  },
  CLOSED_PAGE_NON_BRANDED_TITLE: {
    id: 'SurveyClosed.nonBrandedTitle',
    defaultMessage: 'Online Survey Software: Closed Survey',
  },
});

const SurveyClosed = () => {
  const {
    pageOptions: { canonical, collectorId, isNonBranded = false, languageId: surveyLangId },
    locale,
  } = useContext(StaticContext);
  const [showFooter, setShowFooter] = useState(!isNonBranded);

  const { loading, error, data } = useQuery(GetCollectorClosedByIdQuery, {
    context: {
      targetPath: '/lo-graphql/collector',
      headers: {
        'X-Collector-Id': collectorId,
        'X-Language-Id': surveyLangId,
      },
    },
    variables: {
      collectorId,
      surveyLangId,
    },
    errorPolicy: 'all',
  });

  useEffect(() => {
    if (error || data?.collector === null || data?.collector?.closedPage?.isBrandingDisabled) {
      setShowFooter(false);
    }
  }, [data, error]);

  return (
    <ThemeProvider theme={theme}>
      <BasePage includeHeader={false} includeFooter={showFooter} pageId="Survey Closed" legacyWeb="anonweb">
        <Helmet>
          <base target="_top" href="/" />
          <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
          <meta name="description" content={t(COPY.DEFAULT_CLOSED_MESSAGE)} />
          <link rel="canonical" href={canonical} />
        </Helmet>

        {(error || data?.collector === null) && (
          <>
            <Helmet>
              <title>{t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE)}</title>
            </Helmet>
            <NonBrandedSurveyClosed message={t(COPY.DEFAULT_CLOSED_MESSAGE)} />
          </>
        )}
        {loading && (
          <Container flex flexJustify="center" flexAlign="center" p="5">
            <SkeletonLoader height={130} width={400}>
              <circle cx="10" cy="20" r="8" />
              <rect x="25" y="15" rx="5" ry="5" width="350" height="10" />
            </SkeletonLoader>
          </Container>
        )}
        {data && data.collector && (
          <Choose>
            <When condition={isNonBranded || data.collector.closedPage.isBrandingDisabled}>
              <Helmet>
                <title>{t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE)}</title>
              </Helmet>
              <NonBrandedSurveyClosed
                message={(data.collector && data.collector.closedPage.closedMessage) || t(COPY.DEFAULT_CLOSED_MESSAGE)}
              />
            </When>
            <When condition={locale.isEUSubdomain}>
              <Helmet>
                <title>{t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE)}</title>
              </Helmet>
              <EUBrandedSurveyClosed
                message={(data.collector && data.collector.closedPage.closedMessage) || t(COPY.DEFAULT_CLOSED_MESSAGE)}
              />
            </When>
            <Otherwise>
              <Helmet>
                <title>{t(COPY.CLOSED_PAGE_TITLE)}</title>
              </Helmet>
              <BrandedSurveyClosed
                message={(data.collector && data.collector.closedPage.closedMessage) || t(COPY.DEFAULT_CLOSED_MESSAGE)}
              />
            </Otherwise>
          </Choose>
        )}
      </BasePage>
    </ThemeProvider>
  );
};

export default SurveyClosed;
